import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BillingStrategy } from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ActivationPricingService } from '../activation/core/activation-pricing.service';
import { AppEditionService } from '../activation/core/app-edition.service';
import { PricingService } from '../core/billing';
import { User } from '../core/user';
import { BillingSettingsService } from '../shared-billing/settings/billing-settings.service';
import {
  ChangeEditionDialogDataInterface,
  ChangeEditionDialogService,
  EditionInfo,
} from './change-edition-dialog.service';

export interface ChangeEditionDialogResult {
  success: boolean;
  noLongerSatisfiesDependency?: boolean;
}

@Component({
  templateUrl: 'change-edition-dialog.component.html',
  styleUrls: ['change-edition-dialog.component.scss'],
  standalone: false,
})
export class ChangeEditionDialogComponent implements OnInit, OnDestroy {
  editions$: Observable<EditionInfo[]>;
  currentEditionId: string;
  selectedEditionIdControl: UntypedFormControl;
  get selectedEditionId() {
    return this.selectedEditionIdControl.value as string;
  }
  confirmationCheckbox = false;
  submitting = false;
  billingInfoConfigured$: Observable<boolean>;
  instantBilledItems$: Observable<string[]>;
  notInstantBillable = false;
  isTrial = false;
  dependentProductNames: string[] = [];
  wspMultisiteEditionDemo = 'EDITION-W6P8ZVCV';
  wspMultisiteEditionProd = 'EDITION-6VFZW35G';

  hasEmailUsersChecked = true;
  users$: Observable<User[]>;

  satisfiesDependency$: Observable<boolean>;

  private subscriptions: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<ChangeEditionDialogComponent>,
    private dialog: MatDialog,
    private editionsService: AppEditionService,
    private pricingService: PricingService,
    private changeEditionDialogService: ChangeEditionDialogService,
    private snackbarService: SnackbarService,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeEditionDialogDataInterface,
    private activationPricingService: ActivationPricingService,
    private billingSettingsService: BillingSettingsService,
  ) {}

  ngOnInit(): void {
    this.users$ = this.changeEditionDialogService.users$;
    this.selectedEditionIdControl = new UntypedFormControl(this.data.autoSelectEdition || this.data.currentEditionId);
    this.currentEditionId = this.data.currentEditionId;
    this.isTrial = this.data.isTrial;
    this.dependentProductNames = this.data.dependentProductNames;

    // Get the Editions of the product
    const unfilteredEditions$ = this.editionsService.getEditionsForApp(this.data.appId);

    this.editions$ = combineLatest([unfilteredEditions$]).pipe(
      take(1),
      map(([eds]) => {
        const filteredEditions = eds.filter((edition) => {
          // If trial activation, only allow upgrade to paid for current edition, as other options are broken
          // MT-1458 - We now allow upgrades to paid for all editions (RM only)
          return this.isTrial && this.data.appId !== 'RM' ? this.data.currentEditionId === edition.editionId : true;
        });
        return filteredEditions.map((ed) => {
          const editionInfo: EditionInfo = {
            editionTitle: ed.editionName,
            editionId: ed.editionId,
          };
          return editionInfo;
        });
      }),
    );

    this.partnerId$.pipe(tap((partnerId) => this.checkBillingConfiguration(partnerId))).subscribe();

    this.instantBilledItems$ = this.partnerId$.pipe(
      switchMap((partnerId) => this.pricingService.getMultiProductPricing(partnerId, [this.data.appId])),
      map((productPricing) => {
        const instantBilledItems = [];
        // tslint:disable-next-line:forin
        for (const key in productPricing) {
          const pricingRules = productPricing[key].pricingRules;
          let price = 0;
          if (pricingRules && pricingRules.length > 0) {
            price = pricingRules[0].price || 0;
          }
          if (productPricing[key].strategy === BillingStrategy.Instantly && price) {
            instantBilledItems.push(productPricing[key]);
          }
        }
        return instantBilledItems;
      }),
    );

    this.satisfiesDependency$ = combineLatest([this.editions$, this.selectedEditionIdControl.valueChanges]).pipe(
      map(([editions]) => {
        if (this.dependentProductNames.length <= 0) {
          return true;
        }
        const currentEditionIndex = editions.findIndex((edition) => edition.editionId === this.currentEditionId);
        const selectedEditionIndex = editions.findIndex((edition) => edition.editionId === this.selectedEditionId);
        return selectedEditionIndex >= currentEditionIndex;
      }),
      shareReplay(1),
    );

    this.subscriptions.push(
      combineLatest([this.billingInfoConfigured$, this.instantBilledItems$])
        .pipe(
          tap(([billingInfoConfigured, instantBilledItems]) => {
            this.notInstantBillable = !billingInfoConfigured && instantBilledItems.length > 0;
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private checkBillingConfiguration(partnerId: string): void {
    // the merchant info needs to be set up before checking billingInfoConfigured$, this should be refactored later
    if (!this.billingSettingsService.merchantId) {
      this.billingSettingsService.merchantId = partnerId;
    }
    this.billingSettingsService.loadMerchantDetails();
    this.billingSettingsService.loadMerchantCreditCards();
    this.billingInfoConfigured$ = this.activationPricingService.billingInfoConfigured$;
  }

  activateEditionForApp(): void {
    if (this.isTrial) {
      // redirects to confirm order step
      this.changeEditionDialogService.handleTrialUpgradeToPaid(this.data.appId, this.selectedEditionId);
      this.dialogRef.close();
      return;
    }
    this.submitting = true;
    const editionChange$ = this.changeEditionDialogService.activateEditionForApp(
      this.data.appId,
      this.data.activationId,
      this.selectedEditionId,
      this.data.reasons,
      this.hasEmailUsersChecked,
    );
    combineLatest([editionChange$, this.satisfiesDependency$])
      .pipe(take(1))
      .subscribe(
        ([success, satisifiesDependency]) => {
          if (success) {
            const result: ChangeEditionDialogResult = {
              success: success,
              noLongerSatisfiesDependency: !satisifiesDependency,
            };
            this.snackbarService.successSnack('Awesome! Your edition change was submitted successfully.');
            this.dialogRef.close(result);
          } else {
            this.snackbarService.errorSnack('Uh-oh. Looks like something went wrong on our end. Please try again.');
            this.submitting = false;
          }
        },
        (err) => {
          this.snackbarService.errorSnack('Uh-oh. Looks like something went wrong on our end. Please try again.');
          this.submitting = false;
          console.log(err);
        },
      );
  }

  get formattedDependentProductsNames(): string {
    return this.dependentProductNames.join(', ');
  }
}
